// src/pages/onboarding/doctor/steps/StepProfileComplete.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import supabase from "@/services/supabaseClient";
import OnboardingTopBar from "@/components/onboarding/OnboardingTopBar";
import OnboardingSection from "@/components/onboarding/OnboardingSection";
import Button from "@/components/ui/Button";
import { useAuth } from "@/contexts/AuthContext";
import { markSettingsWelcomePending } from "@/lib/settingsWelcomeState.js";
import { canUseOnboardingDraftMode } from "@/lib/onboardingDraftMode";
import { clearProgress } from "@/lib/persistStep";
import { advanceStagingFirstUse } from "@/services/stagingAccessService.js";

const DOCTOR_PROGRESS_KEY = "onb_doctor_v2";

function withTimeout(promise, ms = 10000, label = "Request") {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out`)), ms)
    ),
  ]);
}

export default function StepProfileComplete({ step, total, onBack }) {
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // A completed first-use profile returns to the Settings handoff once, where
  // the established welcome dialog and Fola guidance can take over.
  useEffect(() => {
    let alive = true;

    async function guard() {
      try {
        const {
          data: { user },
          error: uErr,
        } = await withTimeout(supabase.auth.getUser(), 7000, "Session");
        if (uErr) throw uErr;
        if (!user || !alive) return;

        const { data } = await withTimeout(
          supabase
            .from("doctor_profiles")
            .select("onboarding_completed")
            .eq("user_id", user.id)
            .maybeSingle(),
          7000,
          "Onboarding status check"
        );

        if (!alive) return;

        if (data?.onboarding_completed) {
          navigate("/settings/doctor", { replace: true, state: { showSettingsWelcome: true } });
        }
      } catch (e) {
        // silent — we don't block the screen
      }
    }

    guard();
    return () => {
      alive = false;
    };
  }, [navigate]);

  const finishOnboarding = async () => {
    if (saving) return;

    try {
      setSaving(true);
      setErr("");

      const {
        data: { user },
        error: uErr,
      } = await withTimeout(supabase.auth.getUser(), 7000, "Session");
      if (uErr && !canUseOnboardingDraftMode()) throw uErr;
      if (!user) {
        if (canUseOnboardingDraftMode()) {
          navigate("/settings/doctor", { replace: true, state: { showSettingsWelcome: true } });
          return;
        }
        throw new Error("Session expired");
      }

      const { data: existingProfile } = await withTimeout(
        supabase
          .from("doctor_profiles")
          .select(
            "specialties,primary_specialty,specialty,years_of_experience,years_experience"
          )
          .eq("user_id", user.id)
          .maybeSingle(),
        7000,
        "Profile normalization lookup"
      );

      const specialties = Array.isArray(existingProfile?.specialties)
        ? existingProfile.specialties
            .map((v) => String(v || "").trim())
            .filter(Boolean)
        : [];
      const specialtyCandidate =
        String(existingProfile?.primary_specialty || "").trim() ||
        String(existingProfile?.specialty || "").trim() ||
        specialties[0] ||
        null;

      const yearsA = Number(existingProfile?.years_of_experience);
      const yearsB = Number(existingProfile?.years_experience);
      const yearsAValid = Number.isFinite(yearsA) && yearsA > 0;
      const yearsBValid = Number.isFinite(yearsB) && yearsB > 0;

      const normalizedYears =
        (yearsAValid ? Math.round(yearsA) : null) ??
        (yearsBValid ? Math.round(yearsB) : null);

      const patch = {
        verified_status: "pending",
        onboarding_completed: true,
        onboarding_completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      if (specialtyCandidate) {
        patch.primary_specialty = specialtyCandidate;
        patch.specialty = specialtyCandidate;
      }

      if (normalizedYears != null) {
        patch.years_of_experience = normalizedYears;
        patch.years_experience = normalizedYears;
      }

      // ✅ Adult handshake: update → select → single
      const { data, error } = await withTimeout(
        supabase
          .from("doctor_profiles")
          .update(patch)
          .eq("user_id", user.id)
          .select("user_id,onboarding_completed,onboarding_completed_at")
          .single(),
        10000,
        "Final profile save"
      );

      if (error) throw error;
      if (!data?.onboarding_completed) throw new Error("Final save did not persist.");

      const { error: profileError } = await withTimeout(
        supabase
          .from("profiles")
          .upsert(
            {
              user_id: user.id,
              role: "doctor",
              role_selected: true,
              onboarding_completed: true,
              updated_at: new Date().toISOString(),
            },
            { onConflict: "user_id" }
          )
          .select("user_id,onboarding_completed")
          .single(),
        10000,
        "Core profile completion sync"
      );

      if (profileError) throw profileError;

      await advanceStagingFirstUse("completed").catch(() => {});

      await withTimeout(
        supabase
          .from("user_roles")
          .upsert(
            {
              user_id: user.id,
              role: "doctor",
              updated_at: new Date().toISOString(),
            },
            { onConflict: "user_id,role" }
          ),
        7000,
        "Doctor role sync"
      ).catch((roleError) => {
        console.warn("[StepProfileComplete] Doctor role sync skipped:", roleError?.message || roleError);
      });

      if (typeof refreshProfile === "function") {
        await withTimeout(
          Promise.resolve(refreshProfile()),
          5000,
          "Profile refresh"
        ).catch(() => {});
      }

      clearProgress(DOCTOR_PROGRESS_KEY);
      await markSettingsWelcomePending(user.id, "doctor");
      navigate("/settings/doctor", { replace: true, state: { showSettingsWelcome: true } });
    } catch (e) {
      console.error("Final save failed:", e);
      setErr(e?.message || "Final save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-dvh flex flex-col bg-[rgb(var(--clr-surface))]">
      <OnboardingTopBar step={step} total={total} onBack={onBack} />

      <OnboardingSection
          header={null}
          center
          bodyClassName="flex-1 px-4 !min-h-[calc(100dvh-var(--onb-footer-h)-170px)]"
          footer={
            <div className="w-full max-w-[520px] mx-auto px-4 pointer-events-auto">
              <Button fullWidth size="lg" onClick={finishOnboarding} disabled={saving}>
                <span className="text-title-medium">{saving ? "Finalising..." : "Go to settings"}</span>
              </Button>

              {err ? (
                <p className="mt-3 text-center text-body-small text-[rgb(var(--afrimed-danger))]">
                  {err}
                </p>
              ) : null}
            </div>
          }
        >
        {/* ✅ Centered icon + title (no extra copy) */}
        <div className="w-full flex flex-col items-center justify-center text-center">
          <div className="w-[200px] h-[200px] flex items-center justify-center">
            <img
              src="/icons/profile-complete1.svg"
              alt="Complete"
              className="w-[126px] h-[127px] object-contain select-none"
                draggable="false"
              />
            </div>

          <h2 className="mt-8 text-center text-headline-small text-on-surface">
            You’re all set!
          </h2>
        </div>
      </OnboardingSection>
    </div>
  );
}
